import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Plane } from "lucide-react";
import api, { apiError } from "@/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";



export default function Login() {
  const { login, error, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotNote, setForgotNote] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);

  const submitForgot = async () => {
    setForgotLoading(true);
    try { await api.post("/auth/forgot-password", { email: forgotEmail, note: forgotNote }); setForgotDone(true); }
    catch (e) { toast.error(apiError(e)); } finally { setForgotLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/events");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-blue-600 grid place-items-center"><Plane className="w-5 h-5" /></div>
          <span className="font-extrabold text-xl tracking-tight">JastipFlow</span>
        </div>
        <div>
          <h1 className="text-4xl font-black tracking-tight leading-tight">Operasional Jastip<br />Per Event, Tanpa Miss.</h1>
          <p className="mt-4 text-slate-300 max-w-md">Kelola katalog, order, validasi bayar, shopping list, substitusi, biaya, dan profit final — dalam satu dashboard yang cepat dan padat informasi.</p>
        </div>
        <p className="text-xs text-slate-500 font-mono">Event · Katalog · Order · Payment · Shopping · Profit</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Masuk</h2>
          <p className="text-sm text-slate-500 mt-1">Gunakan akun tim Anda untuk masuk.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" autoComplete="email" placeholder="nama@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" data-testid="login-password" autoComplete="current-password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-rose-600">{error}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">
              {loading ? "Memproses..." : "Masuk"}
            </Button>
            <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotEmail(email); setForgotOpen(true); setForgotDone(false); }} className="block w-full text-center text-xs text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline">Lupa password?</button>
          </form>
          <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>Lupa Password</DialogTitle></DialogHeader>
              {forgotDone ? (
                <div className="space-y-3">
                  <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="forgot-success">Permintaan reset diterima. Admin akan mengatur password baru untuk <span className="font-mono">{forgotEmail}</span> dan menghubungi Anda.</p>
                  <DialogFooter><Button data-testid="forgot-close" onClick={() => setForgotOpen(false)} className="bg-slate-900 hover:bg-slate-800">Tutup</Button></DialogFooter>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500">Masukkan email akun Anda. Permintaan akan diteruskan ke Admin untuk diatur password barunya.</p>
                  <div><Label>Email</Label><Input data-testid="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="nama@email.com" /></div>
                  <div><Label>Catatan untuk Admin <span className="text-slate-400 font-normal">(opsional)</span></Label><Input data-testid="forgot-note" value={forgotNote} onChange={(e) => setForgotNote(e.target.value)} placeholder="mis. nomor WA untuk dihubungi" /></div>
                  <DialogFooter><Button data-testid="forgot-submit" disabled={forgotLoading || !forgotEmail} onClick={submitForgot} className="bg-slate-900 hover:bg-slate-800">{forgotLoading ? "Mengirim..." : "Kirim Permintaan"}</Button></DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>
          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-slate-200" /><span className="text-xs text-slate-400 uppercase tracking-wider">atau</span><div className="h-px flex-1 bg-slate-200" />
          </div>
          <Button type="button" variant="outline" data-testid="login-google" onClick={loginWithGoogle} className="w-full gap-2 border-slate-300">
            <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.5 13.2l7.9 6.1C12.3 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.6 5.9c4.4-4.1 7-10.1 7-17.6z"/><path fill="#FBBC05" d="M10.4 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.5 10.8l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.6-5.9c-2.1 1.4-4.8 2.3-8 2.3-6.3 0-11.7-4.1-13.6-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
            Masuk dengan Google
          </Button>
          <p className="text-[11px] text-slate-400 mt-2">Hanya email yang sudah terdaftar sebagai anggota tim yang bisa masuk via Google.</p>
        </div>
      </div>
    </div>
  );
}
